<template>
  <div class="auth-page auth-page--forgot">
    <header class="auth-card__header auth-card__header--forgot">
      <h1 class="auth-card__title auth-card__title--forgot">Mot de passe oublié</h1>
      <p class="auth-card__subtitle auth-card__subtitle--forgot">
        Entrez votre adresse email pour recevoir un lien de réinitialisation.
      </p>
    </header>
    <div class="auth-page__wrap">
      <Transition name="card" appear>
        <div class="auth-card auth-card--forgot">

          <form v-if="!successMessage" class="auth-form" @submit.prevent="submitRequest">
            <div class="auth-form__group">
              <label for="email" class="auth-form__label">Adresse email</label>
              <div class="auth-form__input-wrap">
                <EnvelopeIcon class="auth-form__icon" />
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="auth-form__input"
                  required
                  placeholder="vous@exemple.com"
                  autocomplete="email"
                  @input="clearMessages"
                />
              </div>
              <p v-if="emailError" class="auth-form__field-error">{{ emailError }}</p>
            </div>

            <div v-if="errorMessage" class="auth-form__error">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              <span>{{ errorMessage }}</span>
            </div>

            <button type="submit" class="auth-form__submit" :disabled="loading">
              <span v-if="!loading" class="auth-form__submit-text">Envoyer le lien</span>
              <span v-else class="auth-form__spinner" aria-hidden="true" />
            </button>
          </form>

          <Transition name="fade" mode="out-in">
            <div v-if="successMessage" class="auth-form__success">
              <div class="auth-form__success-icon">
                <CheckCircleIcon class="w-14 h-14 text-emerald-500" />
              </div>
              <p class="auth-form__success-msg">
                Un lien de réinitialisation a été envoyé à votre adresse email.
              </p>
              <p class="auth-form__success-hint">
                Consultez votre boîte de réception et suivez les instructions. Pensez à vérifier les spams. Le lien expire au bout de 1 h 30.
              </p>
            </div>
          </Transition>

          <p class="auth-form__footer">
            Vous vous souvenez de votre mot de passe ?
            <router-link to="/connexion" class="auth-form__link">Se connecter</router-link>
          </p>
        </div>
      </Transition>
    </div>

  </div>
</template>

<script setup>
import { ref } from 'vue';
import { EnvelopeIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/outline';
import axios from 'axios';
import { safeErrorMessage } from '@/utils/errorMessages';
const email = ref('');
const loading = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const emailError = ref('');

function validateEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val || '');
}

function clearMessages() {
  errorMessage.value = '';
  successMessage.value = '';
  emailError.value = '';
}

async function submitRequest() {
  clearMessages();
  if (!email.value) {
    emailError.value = "L'adresse email est requise";
    return;
  }
  if (!validateEmail(email.value)) {
    emailError.value = 'Veuillez entrer une adresse email valide';
    return;
  }
  loading.value = true;
  try {
    await axios.get('/api/db-health/', { timeout: 10000 }).catch(() => {});
    const response = await axios.post('/api/password-reset/', { email: email.value }, { timeout: 120000 });
    successMessage.value = response.data.message || 'Un lien de réinitialisation a été envoyé à votre adresse email.';
    email.value = '';
  } catch (error) {
    // safeErrorMessage reconnaît déjà timeout / pas de connexion / 429 / 500+ via
    // le catalogue central ; le fallback ne sert que pour un cas non catégorisé.
    errorMessage.value = safeErrorMessage(error, 'Une erreur est survenue. Veuillez réessayer plus tard.');
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--surface, #f7f9fc);
  font-family: var(--font-body);
}

.auth-page--forgot .auth-page__wrap {
  width: 100%;
  max-width: 440px;
  flex: 1;
  justify-content: flex-start;
  padding: 0 1rem 2rem;
  margin: -1.5rem auto 0;
}

.auth-page__wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.auth-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--line);
  padding: 2rem 1.75rem;
  width: 100%;
}
.auth-card--forgot {
  border-radius: 16px;
  border-top: 3px solid var(--brand-gold);
}
.card-enter-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.card-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.auth-card__header--forgot {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2.25rem 1.5rem 2.75rem;
  background:
    linear-gradient(180deg, rgba(84, 57, 41, 0.08), transparent 40%),
    var(--brand-brown);
  border-bottom: none;
  border-radius: 0;
  box-sizing: border-box;
  text-align: center;
}
.auth-card__title--forgot {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  color: #f7f1e8 !important;
  -webkit-text-fill-color: #f7f1e8;
  margin: 0 0 0.5rem 0;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.15;
}
.auth-card__subtitle--forgot {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.88);
  margin: 0 auto;
  text-align: center;
  line-height: 1.55;
  max-width: 360px;
}
.auth-form__group {
  margin-bottom: 1.25rem;
}
.auth-form__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  margin-bottom: 0.5rem;
}
.auth-form__input-wrap {
  position: relative;
}
.auth-form__icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #94a3b8;
}
.auth-form__input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.auth-form__input:focus {
  outline: none;
  border-color: #d89c24;
  box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.15);
}
.auth-form__field-error {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: #b91c1c;
}
.auth-form__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
}
.auth-form__submit {
  width: 100%;
  padding: 0 var(--space-5);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-brown);
  background: var(--brand-gold);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
  min-height: 48px;
}
.auth-form__submit:hover:not(:disabled) {
  background: var(--brand-gold-light);
  color: var(--brand-brown);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.auth-form__submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.auth-form__spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid color-mix(in srgb, var(--brand-ink) 25%, transparent);
  border-top-color: var(--brand-ink);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.auth-form__success {
  text-align: center;
  padding: 1rem 0;
}
.auth-form__success-icon {
  margin-bottom: 1rem;
}
.auth-form__success-msg {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
}
.auth-form__success-hint {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.auth-form__footer {
  margin: 1.5rem 0 0;
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}
.auth-form__link {
  font-weight: 500;
  color: #d89c24;
  text-decoration: none;
}
.auth-form__link:hover {
  color: #b8821a;
}
@media (max-width: 480px) {
  .auth-card {
    padding: 1.5rem 1.25rem;
  }
  .auth-form__submit {
    min-height: 52px;
  }
}
</style>
